import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, Save, Settings } from 'lucide-react';
import ImagePicker from '@/components/ImagePicker';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : '/api';

export default function AdminSettings() {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({
    next_litter_date: '',
    next_litter_parents: 'Sia × Caramel',
    next_litter_description: '',
    contact_email: 'lataigasiberia@gmail.com',
    facebook_url: 'https://www.facebook.com/profile.php?id=100094255166375',
    instagram_url: 'https://www.instagram.com/la_taiga_siberia',
    hero_image_url: '',
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API}/settings`);
      if (response.ok) {
        const data = await response.json();
        setSettings(prev => ({
          ...prev,
          ...Object.fromEntries(Object.entries(data).map(([k, v]) => [k, v ?? '']))
        }));
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API}/admin/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': getAuthHeader(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ id: 'main_settings', ...settings })
      });

      if (response.ok) {
        toast.success('Paramètres mis à jour');
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-cream">
      <header className="bg-forest text-white py-4 px-6">
        <div className="max-w-3xl mx-auto flex items-center gap-4">
          <Link to="/admin/dashboard" className="hover:text-sand">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="font-heading text-xl font-bold">Paramètres</h1>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Prochaine portée */}
          <div className="bg-white rounded-2xl p-6 shadow-soft">
            <h2 className="font-heading text-lg font-semibold text-forest mb-4 flex items-center gap-2">
              🐱 Prochaine portée
            </h2>
            
            <div className="space-y-4">
              <div>
                <Label className="font-body font-semibold text-forest">Date prévue</Label>
                <Input
                  type="date"
                  value={settings.next_litter_date}
                  onChange={(e) => setSettings({...settings, next_litter_date: e.target.value})}
                  className="mt-1"
                />
                <p className="text-xs text-forest/50 mt-1">Cette date sera utilisée pour le compte à rebours</p>
              </div>

              <div>
                <Label className="font-body font-semibold text-forest">Parents</Label>
                <Input
                  value={settings.next_litter_parents}
                  onChange={(e) => setSettings({...settings, next_litter_parents: e.target.value})}
                  className="mt-1"
                  placeholder="Ex: Sia × Caramel"
                />
              </div>

              <div>
                <Label className="font-body font-semibold text-forest">Description</Label>
                <Textarea
                  value={settings.next_litter_description}
                  onChange={(e) => setSettings({...settings, next_litter_description: e.target.value})}
                  className="mt-1"
                  placeholder="Informations sur la portée attendue..."
                />
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className="bg-white rounded-2xl p-6 shadow-soft">
            <h2 className="font-heading text-lg font-semibold text-forest mb-4 flex items-center gap-2">
              &#x1f4e7; Contact & Réseaux sociaux
            </h2>
            
            <div className="space-y-4">
              <div>
                <Label className="font-body font-semibold text-forest">Email de contact</Label>
                <Input
                  type="email"
                  value={settings.contact_email}
                  onChange={(e) => setSettings({...settings, contact_email: e.target.value})}
                  className="mt-1"
                />
              </div>

              <div>
                <Label className="font-body font-semibold text-forest">URL Facebook</Label>
                <Input
                  value={settings.facebook_url}
                  onChange={(e) => setSettings({...settings, facebook_url: e.target.value})}
                  className="mt-1"
                  placeholder="https://www.facebook.com/..."
                />
              </div>

              <div>
                <Label className="font-body font-semibold text-forest">URL Instagram</Label>
                <Input
                  value={settings.instagram_url}
                  onChange={(e) => setSettings({...settings, instagram_url: e.target.value})}
                  className="mt-1"
                  placeholder="https://www.instagram.com/..."
                />
              </div>
            </div>
          </div>

          {/* Image d'accueil */}
          <div className="bg-white rounded-2xl p-6 shadow-soft">
            <h2 className="font-heading text-lg font-semibold text-forest mb-4 flex items-center gap-2">
              &#x1f5bc; Image d'accueil (Hero)
            </h2>
            <ImagePicker
              label="Image de fond de la page d'accueil"
              value={settings.hero_image_url}
              onChange={(url) => setSettings({...settings, hero_image_url: url})}
            />
          </div>

          <Button type="submit" disabled={loading} className="bg-forest hover:bg-forest-dark" data-testid="save-settings-btn">
            <Save className="w-4 h-4 mr-2" />
            {loading ? 'Enregistrement...' : 'Enregistrer les paramètres'}
          </Button>
        </form>
      </main>
    </div>
  );
}
